import { IconArrowDown, IconArrowUp, IconBell, IconBellCheck, IconBellOff, IconInfoCircle } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { api, NOTICE_CHANNELS, type Me, type NoticeChannel, type NoticePreferences, type PushKey } from "../api";
import type { Key } from "../i18n";
import { pushState, subscribePush, type PushState } from "../push";
import { useSession } from "../session";
import { Card, ErrorNote } from "./ui";

type Group = "before" | "during" | "after";
const GROUPS: { id: Group; label: Key; hint: Key }[] = [
  { id: "before", label: "noticesBefore", hint: "noticesBeforeHint" },
  { id: "during", label: "noticesDuring", hint: "noticesDuringHint" },
  { id: "after", label: "noticesAfter", hint: "noticesAfterHint" },
];
const CHANNEL_LABEL: Record<NoticeChannel, Key> = { push: "channelPush", whatsapp: "whatsapp", email: "email" };

const isChannel = (v: unknown): v is NoticeChannel => typeof v === "string" && (NOTICE_CHANNELS as string[]).includes(v);

/** `identity.preferences.notices` as stored (may be absent: everything on, default order), normalised for the form. */
export function readNotices(preferences: Record<string, unknown>): Required<NoticePreferences> {
  const raw = preferences.notices && typeof preferences.notices === "object" ? (preferences.notices as Record<string, unknown>) : {};
  const stored = Array.isArray(raw.channels) ? raw.channels.filter(isChannel) : [];
  const channels = [...new Set([...stored, ...NOTICE_CHANNELS])];
  return { before: raw.before !== false, during: raw.during !== false, after: raw.after !== false, channels };
}

/** Trip notices (docs/16 §2.6): the three groups on or off, the channel order, and push on this device (decision 17). */
export function NoticesCard({ me }: { me: Me }) {
  const { t, setMe } = useSession();
  const prefs = readNotices(me.identity.preferences);
  const [state, setState] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [key, setKey] = useState<PushKey | undefined>(undefined);
  const [push, setPush] = useState<PushState | "loading" | "enabling" | "failed">("loading");

  useEffect(() => {
    let alive = true;
    api
      .pushKey()
      .then((k) => alive && setKey(k))
      .catch(() => alive && setKey({ publicKey: null, simulated: true }));
    pushState()
      .then((s) => alive && setPush(s))
      .catch(() => alive && setPush("unsupported"));
    return () => {
      alive = false;
    };
  }, []);

  const save = async (patch: NoticePreferences) => {
    setState("saving");
    try {
      const res = await api.putNotices(patch);
      const notices = res.notices ?? { ...prefs, ...patch };
      setMe({ ...me, identity: { ...me.identity, preferences: { ...me.identity.preferences, notices } } });
      setState("saved");
      window.setTimeout(() => setState("idle"), 2000);
    } catch {
      setState("error");
    }
  };

  const move = (index: number, delta: -1 | 1) => {
    const target = index + delta;
    if (target < 0 || target >= prefs.channels.length) return;
    const next = [...prefs.channels];
    const a = next[index];
    const b = next[target];
    if (!a || !b) return;
    next[index] = b;
    next[target] = a;
    void save({ channels: next });
  };

  const enable = async () => {
    if (!key?.publicKey) return;
    setPush("enabling");
    try {
      setPush(await subscribePush(key.publicKey));
    } catch {
      setPush("failed");
    }
  };

  const busy = state === "saving";
  const canEnable = Boolean(key?.publicKey) && (push === "available" || push === "failed");

  return (
    <Card className="notices-card" id="notices">
      <p className="small">{t("tripNoticesBody")}</p>
      <ul className="list">
        {GROUPS.map((g) => (
          <li key={g.id} className="list-row">
            <label className="checkbox notice-toggle">
              <input type="checkbox" checked={prefs[g.id]} disabled={busy} onChange={(e) => void save({ [g.id]: e.target.checked })} />
              <span className="list-text">
                <strong>{t(g.label)}</strong>
                <span className="muted small">{t(g.hint)}</span>
              </span>
            </label>
          </li>
        ))}
      </ul>

      <h3 className="notices-subtitle">{t("channelOrder")}</h3>
      <ol className="channel-order">
        {prefs.channels.map((c, i) => (
          <li key={c} className="channel-row">
            <span className="channel-rank">{i + 1}</span>
            <span className="list-text">
              <strong>{t(CHANNEL_LABEL[c])}</strong>
            </span>
            <button type="button" className="icon-button" aria-label={t("moveUp")} disabled={busy || i === 0} onClick={() => move(i, -1)}>
              <IconArrowUp size={16} />
            </button>
            <button type="button" className="icon-button" aria-label={t("moveDown")} disabled={busy || i === prefs.channels.length - 1} onClick={() => move(i, 1)}>
              <IconArrowDown size={16} />
            </button>
          </li>
        ))}
      </ol>
      {state === "saved" && <p className="small saved">{t("saved")}</p>}
      {state === "error" && <ErrorNote>{t("somethingWrong")}</ErrorNote>}

      <h3 className="notices-subtitle">{t("thisDevice")}</h3>
      {key?.simulated && (
        <p className="notice small">
          <IconInfoCircle size={16} /> {t("pushSimulated")}
        </p>
      )}
      {push === "subscribed" ? (
        <p className="small saved push-state">
          <IconBellCheck size={18} /> {t("pushEnabled")}
        </p>
      ) : push === "denied" ? (
        <p className="muted small push-state">
          <IconBellOff size={18} /> {t("pushDenied")}
        </p>
      ) : push === "unsupported" ? (
        <p className="muted small push-state">
          <IconBellOff size={18} /> {t("pushUnsupported")}
        </p>
      ) : (
        <>
          {push === "failed" && <ErrorNote>{t("pushFailed")}</ErrorNote>}
          <div>
            <button type="button" className="button button-primary button-small" disabled={!canEnable} onClick={() => void enable()}>
              <IconBell size={16} /> {push === "enabling" ? t("enablingPush") : t("enablePush")}
            </button>
          </div>
        </>
      )}
    </Card>
  );
}
